import { useCallback, useEffect, useState } from "react";
import { CalendarDays, CheckCircle2, Clock, Copy, Eye, Link2, MailCheck, Play, RefreshCw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const STATUS_LABELS = {
  submitted: "Davet kodu oluşturuldu",
  reviewed: "İnceleniyor",
  code_redeemed: "Kod kullanıldı",
  approved: "Program açıldı",
  declined: "Reddedildi",
};

const EMAIL_STATUS_LABELS = {
  pending: "Gönderim sırasında",
  leased: "Gönderiliyor",
  retry_pending: "Yeniden denenecek",
  sent: "E-posta gönderildi",
  dead_letter: "Gönderilemedi",
};

function durationLabel(minutes) {
  const total = Number(minutes);
  if (!Number.isFinite(total) || total < 1) return "—";
  const hours = Math.floor(total / 60);
  const remaining = total % 60;
  if (!hours) return `${remaining} dakika`;
  if (!remaining) return `${hours} saat`;
  return `${hours} saat ${remaining} dakika`;
}

function remainingLabel(readyAt, now) {
  if (!readyAt) return null;
  const remainingMinutes = Math.max(0, Math.ceil((new Date(readyAt).getTime() - now) / 60000));
  return remainingMinutes ? durationLabel(remainingMinutes) : "Hazırlık süresi doldu";
}

function positionLabel(value) {
  if (!value) return "—";
  return value === "Oturma" ? "Oturarak" : value;
}

function meditationCodeLabel(value) {
  const raw = String(value || "").replaceAll("-", "").toUpperCase();
  return raw.length === 6 ? raw.split("").join("-") : value || "—";
}

function PlanBlock({ block, index }) {
  const isPracticeBlock = Boolean(block.esma_name || block.meditation_code);
  if (!isPracticeBlock) {
    return (
      <div className="border border-pleiades-border bg-white p-3">
        <div className="text-[10px] uppercase tracking-wider text-pleiades-blue">Gün {Number(block.offset_days) + 1}</div>
        <h4 className="mt-1 text-[13px] font-semibold">{index + 1}. {block.title}</h4>
        <p className="mt-2 whitespace-pre-wrap text-[12px] leading-6 text-pleiades-slate">{block.body}</p>
      </div>
    );
  }
  return (
    <div className="border border-pleiades-border bg-white p-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-pleiades-blue">
            {block.week_number ? `${block.week_number}. hafta` : `Gün ${Number(block.offset_days) + 1}`}
          </div>
          <h4 className="mt-1 text-[15px] font-semibold">{block.esma_name || "Çalışma"}</h4>
        </div>
        <div className="border border-pleiades-blue/30 bg-pleiades-blue/5 px-3 py-2 text-center">
          <div className="text-[9px] uppercase tracking-wider text-pleiades-muted">Meditasyon kodu</div>
          <div className="mt-1 font-mono text-[15px] font-semibold tracking-[.16em] text-pleiades-blue">{meditationCodeLabel(block.meditation_code)}</div>
        </div>
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3 border-t border-pleiades-border pt-3 text-[11px] sm:grid-cols-3">
        <div><dt className="text-pleiades-muted">Başlangıç</dt><dd className="mt-1 font-medium">{Number(block.offset_days) + 1}. gün</dd></div>
        <div><dt className="text-pleiades-muted">Kod tekrarı</dt><dd className="mt-1 font-medium">{block.code_repeat_count ?? "—"} kez</dd></div>
        <div><dt className="text-pleiades-muted">Tekrar aralığı</dt><dd className="mt-1 font-medium">{block.repeat_interval_seconds ?? "—"} saniye</dd></div>
        <div><dt className="text-pleiades-muted">Sesli çalışma</dt><dd className="mt-1 font-medium">{block.vocal_minutes ?? "—"} dakika</dd></div>
        <div><dt className="text-pleiades-muted">Esma pozisyonu</dt><dd className="mt-1 font-medium">{positionLabel(block.esma_position)}</dd></div>
        <div><dt className="text-pleiades-muted">Meditasyon</dt><dd className="mt-1 font-medium">{block.meditation_minutes ?? "—"} dakika</dd></div>
        <div><dt className="text-pleiades-muted">Meditasyon pozisyonu</dt><dd className="mt-1 font-medium">{positionLabel(block.meditation_position)}</dd></div>
        <div><dt className="text-pleiades-muted">Ortam</dt><dd className="mt-1 font-medium">{block.environment || "—"}</dd></div>
        <div><dt className="text-pleiades-muted">Gözler</dt><dd className="mt-1 font-medium">{block.eyes_closed ? "Kapalı" : "Açık"}</dd></div>
      </dl>
    </div>
  );
}

function PackageSchedule({ review }) {
  const schedule = review.schedule || [];
  const weeks = [];
  for (let index = 0; index < schedule.length; index += 7) {
    weeks.push(schedule.slice(index, index + 7));
  }
  return (
    <section className="border border-pleiades-border bg-pleiades-ivory/40 p-3" aria-label="Paketin tam gün planı">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-[13px] font-semibold"><CalendarDays size={15} className="text-pleiades-blue" />Paketin tam gün planı</div>
          <p className="mt-1 text-[10px] leading-5 text-pleiades-muted">Bütün paket günleri; çalışma, dinlenme ve faz sırasıyla birlikte gösterilir.</p>
        </div>
        <dl className="grid grid-cols-3 gap-3 text-right text-[10px]">
          <div><dt className="text-pleiades-muted">Toplam</dt><dd className="font-semibold">{review.package?.nominal_days ?? schedule.length} gün</dd></div>
          <div><dt className="text-pleiades-muted">Çalışma</dt><dd className="font-semibold">{review.package?.work_days ?? "—"} gün</dd></div>
          <div><dt className="text-pleiades-muted">Dinlenme</dt><dd className="font-semibold">{review.package?.rest_days ?? "—"} gün</dd></div>
        </dl>
      </div>
      <div className="mt-3 space-y-2">
        {weeks.map((week, weekIndex) => (
          <div key={`week-${weekIndex + 1}`} className="grid grid-cols-7 gap-1" aria-label={`${weekIndex + 1}. hafta`}>
            {week.map((day) => (
              <div key={day.program_day} className={`min-h-12 border p-1.5 text-center ${day.day_type === "work" ? "border-pleiades-blue/30 bg-white" : "border-pleiades-border bg-pleiades-sidebar/60"}`}>
                <div className="text-[10px] font-semibold">{day.program_day}</div>
                <div className="mt-0.5 truncate text-[8px] text-pleiades-muted">{day.day_type === "work" ? `Faz ${day.phase_number}` : "Dinlenme"}</div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </section>
  );
}

function activationState(item, now) {
  if (item.plan?.status === "published") {
    return { label: "Aktif", detail: "İlk çalışma kullanıcı panelinde açıldı.", ready: false };
  }
  if (item.plan?.status === "guide_review") {
    return { label: "Aktif et", detail: "Plan hazır; rehber kontrolü ve aktivasyon bekliyor.", ready: true };
  }
  if (!item.offer_id) {
    return { label: "Aktif et", detail: "Kullanıcının davet kodunu girmesi bekleniyor.", ready: false };
  }
  if (!item.questionnaire) {
    return { label: "Aktif et", detail: "Başlangıç anketinin tamamlanması bekleniyor.", ready: false };
  }
  if (item.planning_mode === "guided") {
    return { label: "Plan bekleniyor", detail: "Anket tamamlandı; çalışma GPT üzerinden rehber tarafından hazırlanacak.", ready: false };
  }
  const remaining = remainingLabel(item.questionnaire.preparation_ready_at, now);
  return {
    label: "Aktif et",
    detail: remaining ? `Plan hazırlanıyor · kalan süre ${remaining}` : "Plan hazırlanıyor.",
    ready: false,
  };
}

export default function ProgramRequestsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [review, setReview] = useState(null);
  const [reviewing, setReviewing] = useState("");
  const [publishing, setPublishing] = useState("");
  const [openingEarly, setOpeningEarly] = useState("");
  const [specialPackageMonths, setSpecialPackageMonths] = useState("1");
  const [specialLink, setSpecialLink] = useState(null);
  const [creatingSpecialLink, setCreatingSpecialLink] = useState(false);
  const [planningMode, setPlanningMode] = useState("automatic");
  const [planningAlerts, setPlanningAlerts] = useState([]);
  const [savingPlanningMode, setSavingPlanningMode] = useState(false);
  const [savingOfferMode, setSavingOfferMode] = useState("");
  const [now, setNow] = useState(() => Date.now());

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [requestsResponse, settingsResponse, alertsResponse] = await Promise.all([
        api.get("/admin/program-requests"),
        api.get("/admin/work-planning/settings"),
        api.get("/admin/work-planning/alerts"),
      ]);
      setItems(requestsResponse.data.items || []);
      setPlanningMode(settingsResponse.data.default_mode || "automatic");
      setPlanningAlerts(alertsResponse.data.items || []);
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const intervalId = window.setInterval(() => setNow(Date.now()), 30000);
    return () => window.clearInterval(intervalId);
  }, []);

  const openPlan = async (item) => {
    if (!item.plan?.id) return;
    setReviewing(item.id);
    try {
      const { data } = await api.get(
        `/admin/matrix-automation/plan-drafts/${encodeURIComponent(item.plan.id)}/review`,
      );
      setReview(data);
    } catch (requestError) {
      toast.error(formatApiError(requestError));
    } finally {
      setReviewing("");
    }
  };

  const activate = async (item) => {
    if (review?.draft_id !== item.plan?.id || item.plan?.status !== "guide_review") return;
    setPublishing(item.id);
    try {
      await api.post(
        `/admin/matrix-automation/plan-drafts/${encodeURIComponent(item.plan.id)}/approve-and-publish`,
        {
          expected_hash: item.plan.draft_hash,
          expected_review_hash: review.review_hash,
        },
      );
      setReview(null);
      toast.success("Program aktif edildi; ilk çalışma kullanıcı panelinde açıldı.");
      await load();
    } catch (requestError) {
      toast.error(formatApiError(requestError));
    } finally {
      setPublishing("");
    }
  };

  const openNextEarly = async (item) => {
    if (item.program?.status !== "waiting_next_day") return;
    if (!window.confirm("Sıradaki günlük çalışma planlanan saatten önce açılsın mı?")) return;
    setOpeningEarly(item.id);
    try {
      await api.post(
        `/admin/daily-programs/${encodeURIComponent(item.program.id)}/open-next-early`,
        { expected_version: item.program.version },
      );
      toast.success("Sıradaki çalışma kullanıcı panelinde erken açıldı.");
      await load();
    } catch (requestError) {
      toast.error(formatApiError(requestError));
    } finally {
      setOpeningEarly("");
    }
  };

  const createSpecialLink = async () => {
    setCreatingSpecialLink(true);
    try {
      const { data } = await api.post("/admin/special-invitation-links", {
        package_months: Number(specialPackageMonths),
        expires_in_days: 30,
      });
      setSpecialLink({
        ...data,
        url: `${window.location.origin}/ozel-davet/${encodeURIComponent(data.token)}`,
      });
      toast.success("Tek kullanımlık özel danışan bağlantısı oluşturuldu.");
    } catch (requestError) {
      toast.error(formatApiError(requestError));
    } finally {
      setCreatingSpecialLink(false);
    }
  };

  const copySpecialLink = async () => {
    if (!specialLink?.url) return;
    try {
      await navigator.clipboard.writeText(specialLink.url);
      toast.success("Özel davet bağlantısı kopyalandı.");
    } catch {
      toast.error("Bağlantı kopyalanamadı.");
    }
  };

  const updateDefaultPlanningMode = async (mode) => {
    setSavingPlanningMode(true);
    try {
      await api.put("/admin/work-planning/settings", { mode });
      setPlanningMode(mode);
      toast.success(mode === "guided" ? "Yeni programlar rehber kontrollü hazırlanacak." : "Yeni programlar otomatik hazırlanacak.");
      await load();
    } catch (requestError) {
      toast.error(formatApiError(requestError));
    } finally {
      setSavingPlanningMode(false);
    }
  };

  const updateOfferPlanningMode = async (item, mode) => {
    if (!item.offer_id) return;
    setSavingOfferMode(item.id);
    try {
      await api.put(`/admin/program-offers/${encodeURIComponent(item.offer_id)}/planning-mode`, { mode });
      toast.success(mode === "guided" ? "Danışan rehber kontrollü moda alındı." : "Danışan otomatik moda alındı.");
      await load();
    } catch (requestError) {
      toast.error(formatApiError(requestError));
    } finally {
      setSavingOfferMode("");
    }
  };

  return (
    <div className="space-y-3 p-3 sm:space-y-5 sm:p-5 lg:p-6" data-testid="program-requests-page">
      <div className="flex flex-col gap-2 sm:gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[.14em] text-pleiades-blue">Danışmanlık yönetimi</div>
          <h1 className="mt-1 text-xl font-semibold tracking-tight sm:mt-2 sm:text-2xl">Program Talepleri</h1>
          <p className="mt-1 max-w-2xl text-[11px] leading-5 text-pleiades-slate sm:mt-2 sm:text-[12px] sm:leading-6">
            Davet teslimini, anket sonrası hazırlık süresini ve kullanıcıya gönderilecek çalışma planını tek ekrandan izleyin.
          </p>
        </div>
        <button type="button" onClick={load} className="inline-flex min-h-11 items-center justify-center gap-2 border border-pleiades-border bg-white px-4 text-[12px] focus:outline-none focus-visible:ring-2 focus-visible:ring-pleiades-blue">
          <RefreshCw size={14} /> Yenile
        </button>
      </div>

      <div className="flex gap-3 border-l-2 border-pleiades-blue bg-white px-4 py-3 text-[12px] leading-6 text-pleiades-slate">
        <ShieldCheck size={17} className="mt-1 shrink-0 text-pleiades-blue" />
        <p><strong className="text-pleiades-ink">Aktivasyon sırası:</strong> Davet kodu kullanılır, anket tamamlanır, plan hazırlanır; rehber planı görüntüleyip kontrol ettikten sonra aktif eder.</p>
      </div>

      <section className="grid gap-3 border border-pleiades-border bg-white p-3 sm:grid-cols-[minmax(0,1fr)_240px] sm:items-center sm:p-4" aria-labelledby="planning-mode-heading">
        <div>
          <h2 id="planning-mode-heading" className="text-[14px] font-semibold">Çalışma hazırlama düzeni</h2>
          <p className="mt-1 text-[11px] leading-5 text-pleiades-slate">Rehber kontrollü modda anket sonrası otomasyon çalışma üretmez. Plan, GPT önizlemesi ve rehber onayıyla yayımlanır.</p>
        </div>
        <select
          value={planningMode}
          disabled={savingPlanningMode}
          onChange={(event) => updateDefaultPlanningMode(event.target.value)}
          className="min-h-11 w-full border border-pleiades-border bg-white px-3 text-[12px] font-medium text-pleiades-ink"
          aria-label="Varsayılan çalışma hazırlama düzeni"
        >
          <option value="automatic">Otomatik planlama</option>
          <option value="guided">Rehber kontrollü</option>
        </select>
      </section>

      {planningAlerts.length > 0 && (
        <section className="border border-amber-200 bg-amber-50/60 p-3" aria-label="Yaklaşan çalışma planı ihtiyaçları">
          <div className="text-[12px] font-semibold text-pleiades-ink">Planlama hatırlatmaları · {planningAlerts.length}</div>
          <div className="mt-2 grid gap-2 sm:grid-cols-2">
            {planningAlerts.map((alert, index) => (
              <div key={alert.run_id || `${alert.client_id || "client"}-${index}`} className="border border-amber-200 bg-white px-3 py-2 text-[11px] text-pleiades-slate">
                <strong className="text-pleiades-ink">{alert.message}</strong>
                <span className="mt-0.5 block">Danışan kodu: {alert.client_id}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="border border-pleiades-border bg-white p-3 sm:p-5" aria-labelledby="special-invitation-heading">
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center border border-pleiades-blue/25 bg-pleiades-blue/5 text-pleiades-blue"><Link2 size={17} /></div>
          <div>
            <h2 id="special-invitation-heading" className="text-[15px] font-semibold">Özel danışan bağlantısı</h2>
            <p className="mt-1 text-[11px] leading-5 text-pleiades-slate">Paketi seçin. Bağlantıyla kayıt olan ilk danışan doğrudan başlangıç anketine geçer.</p>
          </div>
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-[minmax(0,220px)_auto] sm:items-end">
          <label className="text-[12px] text-pleiades-slate">
            Paket
            <select aria-label="Paket" value={specialPackageMonths} onChange={(event) => setSpecialPackageMonths(event.target.value)} className="mt-1.5 min-h-11 w-full border border-pleiades-border bg-white px-3 text-[13px] text-pleiades-ink">
              <option value="1">1 aylık paket</option>
              <option value="3">3 aylık paket</option>
              <option value="6">6 aylık paket</option>
              <option value="12">12 aylık paket</option>
            </select>
          </label>
          <button type="button" onClick={createSpecialLink} disabled={creatingSpecialLink} className="inline-flex min-h-11 items-center justify-center gap-2 bg-pleiades-blue px-5 text-[12px] font-semibold text-white disabled:opacity-40">
            <Link2 size={14} /> {creatingSpecialLink ? "Oluşturuluyor…" : "Özel bağlantı oluştur"}
          </button>
        </div>
        {specialLink && (
          <div className="mt-4 border border-pleiades-blue/25 bg-pleiades-blue/5 p-3">
            <div className="text-[10px] uppercase tracking-wider text-pleiades-muted">Tek kullanımlık bağlantı · {specialLink.package_months} ay</div>
            <div className="mt-2 break-all font-mono text-[11px] leading-5 text-pleiades-ink">{specialLink.url}</div>
            <button type="button" onClick={copySpecialLink} className="mt-3 inline-flex min-h-10 items-center justify-center gap-2 border border-pleiades-border bg-white px-4 text-[12px]"><Copy size={14} /> Bağlantıyı kopyala</button>
          </div>
        )}
      </section>

      {error && <div role="alert" className="border border-red-200 bg-red-50 p-4 text-[12px] text-red-700">{error}</div>}

      <div className="space-y-3">
        {loading && <div className="border border-pleiades-border bg-white p-6 text-[12px] text-pleiades-muted">Talepler yükleniyor…</div>}
        {!loading && items.length === 0 && <div className="border border-pleiades-border bg-white p-6 text-[12px] text-pleiades-muted">Henüz program talebi yok.</div>}
        {items.map((item) => {
          const activation = activationState(item, now);
          const planOpen = Boolean(item.plan?.id && review?.draft_id === item.plan.id);
          return (
            <article key={item.id} className="border border-pleiades-border bg-white p-3 sm:p-5">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-pleiades-muted">
                    <Clock size={13} /> {new Date(item.created_at).toLocaleString("tr-TR")}
                  </div>
                  <h2 className="mt-2 text-[16px] font-semibold">{item.client_name}</h2>
                  <p className="mt-1 text-[12px] text-pleiades-slate">{item.client_email || "E-posta bilgisi yok"}</p>
                  <dl className="mt-4 grid grid-cols-2 gap-x-5 gap-y-3 text-[12px] sm:grid-cols-4">
                    <div><dt className="text-pleiades-muted">Program</dt><dd className="mt-1 font-medium">{item.package_months} ay</dd></div>
                    <div><dt className="text-pleiades-muted">Durum</dt><dd className="mt-1 font-medium">{STATUS_LABELS[item.status] || item.status}</dd></div>
                    <div><dt className="text-pleiades-muted">Davet e-postası</dt><dd className="mt-1 font-medium">{EMAIL_STATUS_LABELS[item.invitation_email_status] || "Henüz oluşturulmadı"}</dd></div>
                    <div><dt className="text-pleiades-muted">Planlanan hazırlık</dt><dd className="mt-1 font-medium">{durationLabel(item.questionnaire?.preparation_delay_minutes)}</dd></div>
                  </dl>
                  {item.offer_id && (
                    <label className="mt-3 block max-w-[230px] text-[11px] text-pleiades-muted">
                      Bu danışanın planlama düzeni
                      <select
                        value={item.planning_mode || "automatic"}
                        disabled={savingOfferMode === item.id || Boolean(item.program?.completed_steps)}
                        onChange={(event) => updateOfferPlanningMode(item, event.target.value)}
                        className="mt-1 min-h-10 w-full border border-pleiades-border bg-white px-3 text-[12px] text-pleiades-ink disabled:opacity-50"
                      >
                        <option value="automatic">Otomatik</option>
                        <option value="guided">Rehber kontrollü</option>
                      </select>
                    </label>
                  )}
                </div>
                <div className="min-w-0 border border-pleiades-border bg-pleiades-ivory/50 p-3 lg:w-[310px]">
                  <div className="flex gap-2 text-[12px] text-pleiades-slate"><MailCheck size={17} className="shrink-0 text-pleiades-blue" /><span>{activation.detail}</span></div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {item.plan?.id && <button type="button" onClick={() => openPlan(item)} disabled={reviewing === item.id} className="inline-flex min-h-11 items-center justify-center gap-2 border border-pleiades-border bg-white px-4 text-[12px] disabled:opacity-40"><Eye size={14} />{reviewing === item.id ? "Açılıyor…" : "Çalışma planını gör"}</button>}
                    <button type="button" onClick={() => activate(item)} disabled={!activation.ready || !planOpen || publishing === item.id} className="inline-flex min-h-11 items-center justify-center gap-2 bg-pleiades-blue px-4 text-[12px] text-white disabled:cursor-not-allowed disabled:opacity-40"><CheckCircle2 size={14} />{publishing === item.id ? "Aktif ediliyor…" : activation.label}</button>
                    {item.program?.status === "waiting_next_day" && <button type="button" onClick={() => openNextEarly(item)} disabled={openingEarly === item.id} className="inline-flex min-h-11 items-center justify-center gap-2 border border-pleiades-blue bg-white px-4 text-[12px] text-pleiades-blue disabled:opacity-40"><Play size={14} />{openingEarly === item.id ? "Açılıyor…" : "Sıradaki çalışmayı erken aç"}</button>}
                  </div>
                  {activation.ready && !planOpen && <p className="mt-2 text-[10px] text-pleiades-muted">Aktivasyondan önce çalışma planını açıp kontrol edin.</p>}
                </div>
              </div>

              {planOpen && <section className="mt-4 border-t border-pleiades-border pt-4" aria-label={`${item.client_name} çalışma planı`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div><h3 className="text-[13px] font-semibold">Kullanıcıya gönderilecek çalışma planı</h3><p className="mt-1 text-[10px] text-pleiades-muted">{review.status === "published" ? "Yayınlandı" : "Aktivasyon bekliyor"} · katalog {review.catalog_version}</p></div>
                  <button type="button" onClick={() => setReview(null)} className="min-h-10 border border-pleiades-border bg-white px-3 text-[11px]">Planı kapat</button>
                </div>
                <div className="mt-3"><PackageSchedule review={review} /></div>
                <div className="mt-3 grid gap-3 lg:grid-cols-2">
                  {(review.blocks || []).map((block, index) => <PlanBlock key={`${block.template_id}-${block.offset_days}`} block={block} index={index} />)}
                </div>
              </section>}
            </article>
          );
        })}
      </div>
    </div>
  );
}
